import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Col, Container, Form, Row, Spinner, Card } from "react-bootstrap";
import DynamicBreadcrumb from "../header/breadcrumb";
import orderLocationApi from "../../services/orderLocationApi";
import { loadGoogleMaps } from "../../utils/googleMapsLoader";

const normalizeGeojson = (geojson) => {
  if (!geojson) return null;
  if (typeof geojson === "string") {
    try { return JSON.parse(geojson); } catch (e) { return null; }
  }
  return geojson;
};

const geojsonToPath = (geojson) => {
  const geo = normalizeGeojson(geojson);
  const ring = geo?.coordinates?.[0] || [];
  return ring.map(([lng, lat]) => ({ lat: Number(lat), lng: Number(lng) }));
};

const pointInPolygon = (latitude, longitude, coordinates = []) => {
  if (!Array.isArray(coordinates) || coordinates.length < 3) return false;

  const x = Number(longitude);
  const y = Number(latitude);
  let inside = false;

  for (let i = 0, j = coordinates.length - 1; i < coordinates.length; j = i++) {
    const xi = Number(coordinates[i][0]);
    const yi = Number(coordinates[i][1]);
    const xj = Number(coordinates[j][0]);
    const yj = Number(coordinates[j][1]);

    const intersect =
      yi > y !== yj > y &&
      x < ((xj - xi) * (y - yi)) / ((yj - yi) || 1e-12) + xi;

    if (intersect) inside = !inside;
  }

  return inside;
};

const findMatchedZoneFromAllZones = (data) => {
  if (!data?.latitude || !data?.longitude || !Array.isArray(data?.allZones)) return null;

  for (const zone of data.allZones) {
    const geo = normalizeGeojson(zone.geojson);
    const coordinates = geo?.coordinates?.[0] || [];
    if (pointInPolygon(data.latitude, data.longitude, coordinates)) {
      return zone;
    }
  }

  return null;
};
const getZoneCenter = (path = []) => {
  if (!Array.isArray(path) || !path.length) return null;

  let latSum = 0;
  let lngSum = 0;

  path.forEach((point) => {
    latSum += Number(point.lat);
    lngSum += Number(point.lng);
  });

  return {
    lat: latSum / path.length,
    lng: lngSum / path.length,
  };
};

const getBranchLabelName = (zone = {}) => {
  return (
    zone.branchName ||
    zone.branch_name ||
    zone.brch_name ||
    `Branch ${zone.branchId || zone.branch_id || ""}`
  );
};

export default function ZonePointLocator() {
  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const polygonsRef = useRef([]);
  const markersRef = useRef([]);

  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);


  const effectiveMatchedZone =
    result?.matchedZone || findMatchedZoneFromAllZones(result);
  const clearMapObjects = () => {
    polygonsRef.current.forEach((polygon) => polygon.setMap(null));
    markersRef.current.forEach((marker) => marker.setMap(null));
    polygonsRef.current = [];
    markersRef.current = [];
  };

  const drawResult = async (data) => {
    const google = await loadGoogleMaps([]);
    if (!mapDivRef.current) return;

    if (!mapRef.current) {
      mapRef.current = new google.maps.Map(mapDivRef.current, {
        center: { lat: 13.0827, lng: 80.2707 },
        zoom: 11,
        mapTypeControl: true,
        streetViewControl: false,
        fullscreenControl: false,
      });
    }

    clearMapObjects();
    const bounds = new google.maps.LatLngBounds();
    let hasBounds = false;

    const fallbackMatchedZone = data?.matchedZone || findMatchedZoneFromAllZones(data);

    (data?.allZones || []).forEach((zone) => {
      const path = geojsonToPath(zone.geojson);
      if (!path.length) return;

      const isMatched = Number(zone.id) === Number(fallbackMatchedZone?.id);
      const branchLabelName = getBranchLabelName(zone);

      const polygon = new google.maps.Polygon({
        paths: path,
        map: mapRef.current,
        clickable: false,
        editable: false,
        draggable: false,
        strokeColor: isMatched ? "#111827" : "#2563eb",
        fillColor: isMatched ? "#2563eb" : "#60a5fa",
        strokeOpacity: 0.95,
        fillOpacity: isMatched ? 0.35 : 0.12,
        strokeWeight: isMatched ? 4 : 2,
      });

      polygonsRef.current.push(polygon);

      path.forEach((point) => {
        bounds.extend(point);
        hasBounds = true;
      });

      const center = getZoneCenter(path);

      if (center) {
        const labelMarker = new google.maps.Marker({
          position: center,
          map: mapRef.current,
          title: branchLabelName,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 0,
            fillOpacity: 0,
            strokeOpacity: 0,
          },
          label: {
            text: branchLabelName,
            color: isMatched ? "#111827" : "#1d4ed8",
            fontSize: isMatched ? "14px" : "12px",
            fontWeight: isMatched ? "700" : "600",
          },
          zIndex: isMatched ? 999 : 100,
        });

        markersRef.current.push(labelMarker);
      }
    });

    if (data?.latitude && data?.longitude) {
      const marker = new google.maps.Marker({
        position: { lat: Number(data.latitude), lng: Number(data.longitude) },
        map: mapRef.current,
        title: "Search Point",
        label: "P",
      });
      markersRef.current.push(marker);
      bounds.extend(marker.getPosition());
      hasBounds = true;
    }


    const resolvedMatchedZone = data?.matchedZone || findMatchedZoneFromAllZones(data);

    if (resolvedMatchedZone?.branchLatitude && resolvedMatchedZone?.branchLongitude) {
      const branchMarker = new google.maps.Marker({
        position: {
          lat: Number(resolvedMatchedZone.branchLatitude),
          lng: Number(resolvedMatchedZone.branchLongitude),
        },
        map: mapRef.current,
        title: resolvedMatchedZone.branchName || "Matched Branch",
        label: {
          text: getBranchLabelName(resolvedMatchedZone),
          color: "#111827",
          fontSize: "13px",
          fontWeight: "700",
        },
      });
      markersRef.current.push(branchMarker);
      bounds.extend(branchMarker.getPosition());
      hasBounds = true;
    }

    if (hasBounds) {
      mapRef.current.fitBounds(bounds);
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    setError("");
    setResult(null);

    try {
      const response = await orderLocationApi.locatePointInZones({
        latitude,
        longitude,
        activeOnly: true,
      });
      setResult(response?.data || null);
      if (response?.data) {
        await drawResult(response.data);
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to locate point");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGoogleMaps([]).then(() => {
      if (!mapRef.current && mapDivRef.current) {
        mapRef.current = new window.google.maps.Map(mapDivRef.current, {
          center: { lat: 13.0827, lng: 80.2707 },
          zoom: 10,
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: false,
        });
      }
    });
  }, []);

  return (
    <Container fluid className="py-3">
      <DynamicBreadcrumb pathArray={[{ name: "Branch Zone Point Locator", url: null }]} />
      <Row className="g-3 mb-3">
        <Col md={4}>
          <Form.Group>
            <Form.Label>Latitude</Form.Label>
            <Form.Control value={latitude} onChange={(e) => setLatitude(e.target.value)} placeholder="13.0372" />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>Longitude</Form.Label>
            <Form.Control value={longitude} onChange={(e) => setLongitude(e.target.value)} placeholder="80.2128" />
          </Form.Group>
        </Col>
        <Col md={4} className="d-flex align-items-end">
          <Button onClick={handleSearch} disabled={loading || !latitude || !longitude}>
            {loading ? <Spinner size="sm" animation="border" /> : "Locate Point"}
          </Button>
        </Col>
      </Row>

      {error ? <Alert variant="danger">{error}</Alert> : null}

      <Row className="g-3">
        <Col md={8}>
          <div ref={mapDivRef} style={{ width: "100%", height: 600, borderRadius: 12, overflow: "hidden" }} />
        </Col>
        <Col md={4}>
          <Card>
            <Card.Body>
              <h4 className="mb-3">Result</h4>

              {effectiveMatchedZone ? (
                <>
                  <div className="text-success fw-bold mb-3">Inside Active Zone</div>

                  <div><strong>Branch Name:</strong> {effectiveMatchedZone.branchName || "-"}</div>
                  <div><strong>Branch ID:</strong> {effectiveMatchedZone.branchId || "-"}</div>
                  <div><strong>Zone Type:</strong> {effectiveMatchedZone.zoneType || "-"}</div>
                  <div><strong>Priority:</strong> {effectiveMatchedZone.priority ?? "-"}</div>
                  <div><strong>Branch Latitude:</strong> {effectiveMatchedZone.branchLatitude || "-"}</div>
                  <div><strong>Branch Longitude:</strong> {effectiveMatchedZone.branchLongitude || "-"}</div>

                  <hr />

                  <div><strong>Customer Latitude:</strong> {result.latitude}</div>
                  <div><strong>Customer Longitude:</strong> {result.longitude}</div>
                </>
              ) : (
                <>
                  <div className="text-danger fw-bold mb-3">Outside All Active Zones</div>
                  <div><strong>Customer Latitude:</strong> {result?.latitude ?? "-"}</div>
                  <div><strong>Customer Longitude:</strong> {result?.longitude ?? "-"}</div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
